import { Armchair, CalendarDays, CheckCircle2, ClipboardList, Sun, Sunrise, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppointmentQueue, useTodayQueue, useWorkingDay } from '@/features/appointments';
import { useWorkingHours } from '@/features/settings';
import { todayWorkingDayKey, tomorrowWorkingDayKey } from '@/shared/lib/working-day';
import { DatePicker } from '@/shared/ui/DatePicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';
import {
  HEADER_CHIP_CLASS,
  HEADER_CHIP_ICON_WELL,
  HEADER_METRIC_PILL,
  QUEUE_TONE,
  WORKING_DAY_NAV_ICON,
  WORKING_DAY_NAV_TILE,
  WORKING_DAY_SEGMENT,
  WORKING_DAY_SEGMENT_ACTIVE,
  WORKING_DAY_SEGMENT_TRACK,
  WORKING_DAY_STAT_TILE,
  WORKING_DAY_STAT_TONES,
} from './header-chrome';
import { formatQueuePulseLabel, listFloorPreview } from './header-queue';
import { buildWorkingDayStatLink } from './working-day-stat-nav';
import {
  formatDayHoursLine,
  summarizeDayVisits,
  workingDayPopoverTitle,
} from './working-day-summary';

const STAT_CELLS = [
  { key: 'visits', label: 'Visits', Icon: CalendarDays, tone: WORKING_DAY_STAT_TONES.visits },
  { key: 'waiting', label: 'Waiting', Icon: Users, tone: WORKING_DAY_STAT_TONES.waiting },
  { key: 'inChair', label: 'In chair', Icon: Armchair, tone: WORKING_DAY_STAT_TONES.inChair },
  { key: 'done', label: 'Done', Icon: CheckCircle2, tone: WORKING_DAY_STAT_TONES.done },
] as const satisfies ReadonlyArray<{
  key: 'visits' | 'waiting' | 'inChair' | 'done';
  label: string;
  Icon: typeof CalendarDays;
  tone: string;
}>;

/**
 * Combined today + floor pulse — day stats, live queue, and day peek in one chip.
 */
export function WorkingDayChip({ className }: { className?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const todayKey = todayWorkingDayKey();
  const tomorrowKey = tomorrowWorkingDayKey();
  const { setDateKey } = useWorkingDay();

  const [previewKey, setPreviewKey] = useState(todayKey);

  useEffect(() => {
    if (open) setPreviewKey(todayWorkingDayKey());
  }, [open]);

  const todayFloor = useTodayQueue();
  const previewQueue = useAppointmentQueue(previewKey);
  const hoursQuery = useWorkingHours();

  const floorRows = todayFloor.data?.queue ?? [];
  const pulse = useMemo(() => formatQueuePulseLabel(floorRows), [floorRows]);
  const floorPreview = useMemo(() => listFloorPreview(floorRows, 5), [floorRows]);
  const waitingPreview = floorPreview.filter((r) => r.status === 'checked_in');
  const inChairPreview = floorPreview.filter((r) => r.status === 'in_progress');

  const previewStats = useMemo(() => {
    return summarizeDayVisits(previewQueue.data?.data ?? []);
  }, [previewQueue.data?.data]);

  const hoursLine = useMemo(
    () => formatDayHoursLine(previewKey, hoursQuery.data),
    [previewKey, hoursQuery.data],
  );

  const chipTitle = [
    todayFloor.isLoading
      ? 'Loading floor…'
      : pulse.totalActive === 0
        ? 'Floor clear'
        : `${pulse.waiting} waiting · ${pulse.inChair} in chair`,
    formatDayHoursLine(todayKey, hoursQuery.data),
  ]
    .filter(Boolean)
    .join(' · ');

  const title = workingDayPopoverTitle(previewKey);
  const isPreviewToday = previewKey === todayKey;
  const isPreviewTomorrow = previewKey === tomorrowKey;

  const chipTone =
    pulse.totalActive === 0
      ? ''
      : pulse.hasLongWait
        ? 'bg-coral-50/80 hover:bg-coral-50'
        : pulse.inChair > 0 && pulse.waiting === 0
          ? 'bg-orange-50/80 hover:bg-orange-50'
          : 'bg-amber-50/80 hover:bg-amber-50';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(HEADER_CHIP_CLASS, 'hidden sm:inline-flex', chipTone, className)}
          aria-label={`Today. ${chipTitle}. ${pulse.text}`}
          title={chipTitle}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className="relative flex items-center gap-1.5">
            <span
              className={cn(
                HEADER_CHIP_ICON_WELL,
                pulse.waiting > 0 && !pulse.hasLongWait && 'bg-amber-100 text-amber-800',
                pulse.hasLongWait && 'bg-coral-100 text-coral-700',
                pulse.inChair > 0 && pulse.waiting === 0 && 'bg-orange-100 text-orange-800',
              )}
            >
              <CalendarDays className="size-3.5" aria-hidden />
              {pulse.totalActive > 0 ? (
                <span
                  className={cn(
                    'header-queue-pulse-dot absolute -right-0.5 -top-0.5 size-1.5 rounded-full',
                    pulse.hasLongWait
                      ? 'bg-coral-500'
                      : pulse.waiting > 0
                        ? 'bg-amber-500'
                        : 'bg-orange-500',
                  )}
                  aria-hidden
                />
              ) : null}
            </span>
            <span className="text-[12px] font-semibold text-ink-800">Today</span>
            {todayFloor.isLoading ? (
              <span className="text-ink-400">…</span>
            ) : pulse.totalActive === 0 ? (
              <span className="font-medium text-ink-500">Clear</span>
            ) : (
              <span className="flex items-center gap-0.5" aria-live="polite">
                {pulse.waiting > 0 ? (
                  <span className={cn(HEADER_METRIC_PILL, QUEUE_TONE.waiting.chip)}>
                    {pulse.waiting}
                  </span>
                ) : null}
                {pulse.inChair > 0 ? (
                  <span className={cn(HEADER_METRIC_PILL, QUEUE_TONE.inChair.chip)}>
                    {pulse.inChair}
                  </span>
                ) : null}
              </span>
            )}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[20rem] overflow-hidden p-0" sideOffset={8}>
        <div className="relative border-b hairline bg-gradient-to-br from-brand-50 via-white to-amber-50/50 px-3.5 py-3">
          <div
            className="pointer-events-none absolute -right-8 -top-10 size-28 rounded-full bg-brand-100/40 blur-2xl"
            aria-hidden
          />
          <p className="relative text-[13px] font-semibold text-ink-900">{title}</p>
          <p className="relative mt-0.5 text-[11px] text-ink-500">
            {hoursLine ?? 'Clinic hours unavailable'}
          </p>
        </div>

        <div className="space-y-3 p-3">
          <div className="grid grid-cols-4 gap-1.5">
            {STAT_CELLS.map((cell) => (
              <Link
                key={cell.key}
                to={buildWorkingDayStatLink(previewKey, cell.key)}
                className={cn(WORKING_DAY_STAT_TILE, cell.tone)}
                aria-label={`${previewStats[cell.key]} ${cell.label.toLowerCase()} — view appointments`}
                onClick={() => {
                  setDateKey(previewKey);
                  setOpen(false);
                }}
              >
                <cell.Icon className="mx-auto mb-1 size-3.5 opacity-80" aria-hidden />
                <div className="text-[15px] font-bold tabular-nums leading-none">
                  {previewQueue.isLoading ? '—' : previewStats[cell.key]}
                </div>
                <div className="mt-1 text-[9px] font-semibold uppercase tracking-wide opacity-75">
                  {cell.label}
                </div>
              </Link>
            ))}
          </div>

          {isPreviewToday ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1.5">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                    QUEUE_TONE.waiting.badge,
                  )}
                >
                  <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
                  {pulse.waiting} waiting
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                    QUEUE_TONE.inChair.badge,
                  )}
                >
                  <span className="size-1.5 rounded-full bg-orange-500" aria-hidden />
                  {pulse.inChair} in chair
                </span>
              </div>

              {floorPreview.length > 0 ? (
                <div className="max-h-44 space-y-2 overflow-y-auto nice-scroll rounded-xl border border-surface-200/80 bg-surface-50/30 p-2">
                  {waitingPreview.length > 0 ? (
                    <section>
                      <p className="mb-1.5 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                        Waiting
                      </p>
                      <ul className="flex flex-col gap-1.5">
                        {waitingPreview.map((row) => (
                          <li key={row.id}>
                            <Link
                              to={`/appointments/${row.id}`}
                              className={cn(
                                'flex items-center justify-between gap-2 px-2.5 py-1.5 text-[12px] text-ink-800',
                                QUEUE_TONE.waiting.row,
                              )}
                              onClick={() => setOpen(false)}
                            >
                              <span className="min-w-0 truncate font-medium">{row.patientName}</span>
                              <span className="shrink-0 tabular-nums text-[10px] text-amber-700">
                                {row.waitLabel}
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                  {inChairPreview.length > 0 ? (
                    <section>
                      <p className="mb-1.5 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700">
                        In chair
                      </p>
                      <ul className="flex flex-col gap-1.5">
                        {inChairPreview.map((row) => (
                          <li key={row.id}>
                            <Link
                              to={`/appointments/${row.id}`}
                              className={cn(
                                'flex items-center justify-between gap-2 px-2.5 py-1.5 text-[12px] text-ink-800',
                                QUEUE_TONE.inChair.row,
                              )}
                              onClick={() => setOpen(false)}
                            >
                              <span className="min-w-0 truncate font-medium">{row.patientName}</span>
                              <span className="shrink-0 text-[10px] font-medium text-orange-700">
                                In chair
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ) : null}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-surface-200 px-3 py-2.5 text-center text-[11px] text-ink-500">
                  Floor is clear
                </p>
              )}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-surface-200 px-3 py-2.5 text-center text-[11px] text-ink-500">
              Live floor list is for today only
            </p>
          )}

          <div className="space-y-1.5">
            <div className={WORKING_DAY_SEGMENT_TRACK} role="group" aria-label="Quick day">
              <button
                type="button"
                className={isPreviewToday ? WORKING_DAY_SEGMENT_ACTIVE : WORKING_DAY_SEGMENT}
                aria-pressed={isPreviewToday}
                onClick={() => setPreviewKey(todayKey)}
              >
                <Sun className="size-3 shrink-0" aria-hidden />
                Today
              </button>
              <button
                type="button"
                className={isPreviewTomorrow ? WORKING_DAY_SEGMENT_ACTIVE : WORKING_DAY_SEGMENT}
                aria-pressed={isPreviewTomorrow}
                onClick={() => setPreviewKey(tomorrowKey)}
              >
                <Sunrise className="size-3 shrink-0" aria-hidden />
                Tomorrow
              </button>
            </div>
            <DatePicker
              value={previewKey}
              onChange={(next) => setPreviewKey(next)}
              placeholder="Pick a date"
              triggerClassName="h-8 rounded-lg border-surface-200/80 bg-white px-2.5 text-[11px] shadow-soft"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1 border-t hairline bg-surface-50/30 p-1.5">
          <Link
            to="/appointments/waiting-room"
            className={WORKING_DAY_NAV_TILE}
            onClick={() => setOpen(false)}
          >
            <span className={WORKING_DAY_NAV_ICON}>
              <ClipboardList className="size-3" aria-hidden />
            </span>
            Waiting room
          </Link>
          <Link
            to="/appointments/calendar"
            className={WORKING_DAY_NAV_TILE}
            onClick={() => {
              setDateKey(previewKey);
              setOpen(false);
            }}
          >
            <span className={WORKING_DAY_NAV_ICON}>
              <CalendarDays className="size-3" aria-hidden />
            </span>
            Calendar
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
